import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas } from "@/lib/seo";

const SITE = "https://shopify-freelancer.net";
const PATH = "/ratgeber/shopify-vs-woocommerce";
const TITLE = "Shopify vs. WooCommerce: Kosten, Conversion, Aufwand";
const DESCRIPTION =
  "Shopify oder WooCommerce? Ehrlicher Vergleich zu Kosten, Wartung, Ladezeit und Kaufabschlussrate, inklusive Erfahrung aus einer Migration von WooCommerce zu Shopify.";

const faqs = [
  {
    q: "Was ist besser: Shopify oder WooCommerce?",
    a: "Shopify eignet sich für Händler, die verkaufen statt warten wollen: gehostet, sicherer Checkout, planbare Kosten. WooCommerce lohnt sich bei sehr speziellen Anforderungen und vorhandenem WordPress-Know-how oder eigenem Entwicklerteam.",
  },
  {
    q: "Was ist günstiger, Shopify oder WooCommerce?",
    a: "WooCommerce wirkt günstiger, weil die Software kostenlos ist. Nach Hosting, Plugins, Wartung und Sicherheitsupdates liegen beide Systeme bei vergleichbaren Gesamtkosten, Shopify verteilt sie nur planbar auf den Monat.",
  },
  {
    q: "Ist die Conversion-Rate bei Shopify höher?",
    a: "Nicht automatisch, aber der Shopify-Checkout ist stark optimiert und schwer zu verschlechtern. Bei Migrationen sehe ich vor allem deshalb Verbesserungen, weil Ladezeit und Checkout-Führung besser werden.",
  },
  {
    q: "Wie aufwendig ist eine Migration von WooCommerce zu Shopify?",
    a: "Für einen Shop mit bis zu 1.000 Produkten üblicherweise drei bis sechs Wochen: Produktdaten und Kunden übertragen, Theme aufbauen, Weiterleitungen setzen, Tracking neu aufsetzen.",
  },
  {
    q: "Verliere ich bei einer Migration SEO-Rankings?",
    a: "Nur bei fehlenden Weiterleitungen. Mit einer vollständigen 301-Weiterleitungsliste für alle alten URLs stabilisieren sich die Rankings in der Regel innerhalb von vier bis acht Wochen.",
  },
];

export const Route = createFileRoute("/ratgeber/shopify-vs-woocommerce")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: articleSchemas({ title: TITLE, description: DESCRIPTION, path: PATH, faqs }),
  }),
});

function Page() {
  return (
    <ArticlePage
      kicker="Ratgeber · Systemwahl"
      title="Shopify vs. WooCommerce: die Entscheidung entlang der Kosten"
      intro="Die Systemfrage wird meist emotional geführt und sollte rechnerisch entschieden werden. Dieser Vergleich stellt Gesamtkosten, Wartungsaufwand und Wirkung auf die Kaufabschlussrate gegenüber."
      source="ratgeber-shopify-vs-woocommerce"
      sections={[
        {
          h: "Der ehrliche Kostenvergleich",
          p: [
            "WooCommerce ist als Software kostenlos, aber nicht wartungsfrei. Hosting, Sicherheitsupdates, Plugin-Lizenzen und ein Ansprechpartner für Ausfälle summieren sich.",
          ],
          list: [
            "Shopify: Plan 27 bis 289 € im Monat, Apps 50 bis 300 €, Wartung inklusive",
            "WooCommerce: Hosting 30 bis 200 €, Plugins 30 bis 200 €, Wartung 100 bis 400 €",
            "Der eigentliche Unterschied liegt nicht im Preis, sondern in der Verantwortung für Ausfälle",
          ],
        },
        {
          h: "Checkout und Kaufabschlussrate",
          p: [
            "Der Shopify-Checkout ist millionenfach getestet und lässt sich kaum verschlimmbessern. Bei WooCommerce ist der Checkout frei gestaltbar, das ist ein Vorteil bei speziellen Anforderungen und ein Risiko bei allem anderen.",
            "In Migrationsprojekten kommt die Verbesserung meist aus zwei Quellen: schnellere Seiten und ein kürzerer, klarerer Kaufabschluss.",
          ],
        },
        {
          h: "Wann WooCommerce die bessere Wahl bleibt",
          p: [
            "Bei stark individueller Preislogik, gewachsenen WordPress-Inhalten mit hohem Traffic, Konfiguratoren mit vielen Abhängigkeiten oder vorhandenem Entwicklerteam ist ein Wechsel selten wirtschaftlich.",
          ],
        },
        {
          h: "Migration: Reihenfolge, die Rankings schützt",
          p: [],
          list: [
            "Bestandsaufnahme aller URLs mit Sichtbarkeit und Umsatz",
            "Produkt-, Kunden- und Bestelldaten übertragen und stichprobenartig prüfen",
            "Theme aufbauen, Produktseite und Warenkorb bewusst neu strukturieren",
            "301-Weiterleitungen für jede alte URL setzen, ohne Ausnahme",
            "Tracking neu aufsetzen und die ersten vier Wochen täglich kontrollieren",
          ],
        },
        {
          h: "Aus der Praxis",
          p: [
            "Ein Golf-Shop ist von WooCommerce auf Shopify umgezogen; die Kaufabschlussrate lag danach deutlich über dem Ausgangswert. Ausschlaggebend waren nicht das System an sich, sondern Ladezeit, mobile Produktseite und ein aufgeräumter Kaufabschluss.",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/cases", label: "Cases ansehen" },
        { to: "/ratgeber/shopify-kosten", label: "Shopify Kosten" },
        { to: "/kontakt", label: "Migration anfragen" },
      ]}
    />
  );
}
